import type { Creature, ElementKey, RunState } from '~/types/game'
import { ELEMENTS } from '~/types/game'
import { effectiveSpeed, grantXp, injureCreature, injuryStacks } from '~/utils/battle'
import { rand } from '~/utils/random'

/**
 * ❓ Mystery EVENTS (v5.39, designed with Javi): authored narrative
 * vignettes a mystery node can reveal instead of its classic rerolls.
 * Ten events, split into GOOD (gifts with at most a small string) and
 * BAD (everything meaningful costs you) — the node rolls the GROUP,
 * then draws an unseen event from that group's per-run bag.
 *
 * DESIGN RULES (Javi's): player CHOICE only, zero dice — every outcome
 * is printed on the button, stat-gated options show their check up
 * front. HP prices are % of Max HP with a floor. An option you cannot
 * afford (gold or HP — you must SURVIVE the payment) renders grayed.
 * Art: one painted scene per event in /ui/events/<id>.webp (art by
 * Javi), shown atop the parchment modal.
 */
export type EventGroup = 'good' | 'bad'

export interface EventCost {
  /** Flat gold price. */
  gold?: number
  /** Gold price as a share of current gold (rounded), with a floor. */
  goldPct?: number
  goldFloor?: number
  /** HP price as a share of MAX HP (rounded), with a floor. Paying may
   * never kill: the option grays out unless hp > cost. */
  hpPct?: number
  hpFloor?: number
  /** 💀 v5.43 (per Javi): a PERMANENT Max-HP price — % of max with a
   * floor. Grays out unless at least MAXHP_SAFETY ceiling would remain;
   * current HP is clamped down to the new ceiling (never below 1). */
  maxHpPct?: number
  maxHpFloor?: number
}

/** The lowest ceiling a Max-HP price may leave behind. */
export const MAXHP_SAFETY = 10

export interface EventOptionDef {
  id: string
  /** Button label (en fallback; localized via tc('event', ...)). */
  label: string
  /** One-line printed outcome (en fallback). */
  detail: string
  cost?: EventCost
  /** Target step after choosing: pick roster creature(s) / an element. */
  picker?: 'creature' | 'creature-pair' | 'creature-element'
  /** Which roster creatures qualify as targets (default: all). */
  targetEligible?: (creature: Creature) => boolean
  /** Extra availability gate beyond cost (e.g. someone must be hurt).
   * ⚠️ v5.71 rule (per Javi's ferry report): NEVER gate an option just
   * because its BONUS would be wasted — that forces the player into the
   * costlier choice. Gate only when the option would be meaningless or
   * incoherent, and always ship a `requires` line so the gray explains
   * itself. */
  eligible?: (state: RunState) => boolean
  /** Why this option is grayed, printed under it when it is (en
   * fallback; localized via tc('event', id, `opt-X-requires`)). Every
   * option carrying `eligible`/`targetEligible` MUST have one — the
   * i18n audit and the roll tests both check for it. */
  requires?: string
  /** 🤕 v5.41 (per Javi: "make injury more relevant"): this option ALSO
   * injures on ascensions where injuries run — the apply() calls
   * injureCreature (which no-ops when inactive) and the modal appends
   * `injuryRider` to the printed outcome only when it's real. */
  injuryRider?: string
  /** Apply the outcome. targets = picked creatures (if picker), element
   * = picked element (creature-element). Returns the result line shown
   * on the closing beat (en fallback; localized per option id). */
  apply: (state: RunState, targets: Creature[], element?: ElementKey) => void
}

export interface EventDef {
  id: string
  group: EventGroup
  /** Painted scene. */
  art: string
  name: string
  /** The vignette (en fallback). */
  body: string
  options: EventOptionDef[]
}

/** 🏁 The Rival Dispatcher's finish line: a runner this fast wins the purse. */
export const RACE_WIN_SPEED = 3

/** 👟 v7.39 (per Javi: "Dont cap speed"): an event's "+N Speed" is a plain
 * gain. The old SPEED_CAP of 3 predated the speed rescale (innate Speed now
 * runs to 4), so events could not raise anyone past 3 - and min(3, speed + 1)
 * even LOWERED a Speed-4 creature until the next load's innate floor. Speed
 * is uncapped everywhere else already (Pacesetter, Swift Boots, water). */
export function raiseSpeed(c: { speed: number }, n: number) {
  c.speed += n
}

/** % -of-max HP price with a floor (Javi: "% based with a floor"). */
export function eventHpCost(state: RunState, pct: number, floor: number): number {
  return Math.max(floor, Math.round(state.maxHp * pct))
}

/** 💀 Permanent Max-HP price — same % + floor shape. */
export function eventMaxHpCost(state: RunState, pct: number, floor: number): number {
  return Math.max(floor, Math.round(state.maxHp * pct))
}

export function eventGoldCost(state: RunState, cost: EventCost): number {
  if (cost.gold) return cost.gold
  if (cost.goldPct) return Math.max(cost.goldFloor ?? 1, Math.round(state.gold * cost.goldPct))
  return 0
}

/** Can this option be paid AND survived? (grayed out otherwise) */
export function canAffordOption(state: RunState, option: EventOptionDef): boolean {
  const c = option.cost
  if (c) {
    if (eventGoldCost(state, c) > state.gold) return false
    if (c.hpPct !== undefined && state.hp <= eventHpCost(state, c.hpPct, c.hpFloor ?? 1)) return false
    if (c.maxHpPct !== undefined
      && state.maxHp - eventMaxHpCost(state, c.maxHpPct, c.maxHpFloor ?? 1) < MAXHP_SAFETY) return false
  }
  if (option.eligible && !option.eligible(state)) return false
  // A picker option needs at least one qualifying target.
  if (option.picker && option.targetEligible
    && !state.roster.some(cr => option.targetEligible!(cr))) return false
  if (option.picker === 'creature-pair'
    && state.roster.filter(cr => option.targetEligible?.(cr) ?? true).length < 2) return false
  return true
}

/**
 * 🚪 v5.71 SOFTLOCK GUARD (found auditing Javi's ferry report): two
 * events price BOTH their options (tithe, ferry), so a broke and
 * bleeding crew could face a card where every button is gray — and the
 * modal has no dismissal, which strands the run for good. When this
 * returns true the card shows a free way out instead.
 */
export function eventStranded(state: RunState, event: EventDef): boolean {
  return !event.options.some(o => canAffordOption(state, o))
}

export function payOption(state: RunState, option: EventOptionDef): void {
  const c = option.cost
  if (!c) return
  const gold = eventGoldCost(state, c)
  if (gold > 0) state.goldSpent = (state.goldSpent ?? 0) + Math.min(state.gold, gold) // 🧘 v6.54
  state.gold = Math.max(0, state.gold - gold)
  if (c.hpPct !== undefined) {
    state.hp = Math.max(1, state.hp - eventHpCost(state, c.hpPct, c.hpFloor ?? 1))
  }
  if (c.maxHpPct !== undefined) {
    state.maxHp = Math.max(1, state.maxHp - eventMaxHpCost(state, c.maxHpPct, c.maxHpFloor ?? 1))
    state.hp = Math.max(1, Math.min(state.hp, state.maxHp))
  }
}

const highestElement = (c: Creature): ElementKey =>
  ELEMENTS.reduce((best, e) => (c.stats[e] > c.stats[best] ? e : best), ELEMENTS[0]!)

// 🌿 v7.39 (per Javi, from his brother's playtest): every element, 👟 Speed,
// ⚔️ Fight and 🤕 injury an outcome names carries its glyph, which the
// modal's IconText paints as the game's own icon (elements as the roster
// badges, word kept beside it as elsewhere). A "chosen element" prints
// the whole palette (no-break spaced, so it never splits across lines)
// so the player sees what is on offer before paying.
// Display text only - costs, gates and apply() are untouched.
export const EVENTS: EventDef[] = [
  // ── GOOD: gifts with at most a small string ─────────────────────────
  {
    id: 'bridge', group: 'good', art: '/ui/events/bridge.webp',
    name: 'The Collapsed Bridge',
    body: 'A rope bridge hangs by one cord over the gorge. Someone sure-footed could re-rig it — the climb leaves a mark, but they come back faster than they left.',
    options: [
      {
        id: 'climb', label: 'Send a climber across',
        detail: 'Costs {hp} HP · that creature gains +1 👟 Speed permanently',
        cost: { hpPct: 0.06, hpFloor: 3 },
        picker: 'creature',
        injuryRider: 'the climber also takes 1 🤕 injury',
        // 🌉 v7.39 (per Javi: "let a player choose"): any creature may climb -
        // one already at top Speed just gains nothing (the picker previews it).
        apply: (s, [t]) => {
          if (!t) return
          raiseSpeed(t, 1)
          injureCreature(t, s)
        },
      },
      {
        id: 'walk', label: 'Take the long way around',
        detail: 'Nothing gained, nothing risked',
        apply: () => {},
      },
    ],
  },
  {
    id: 'beacon', group: 'good', art: '/ui/events/beacon.webp',
    name: 'The Beacon Keeper',
    body: 'A dying lantern-keeper presses his flame into your hands and points up the ridge. The climb is brutal — but keepers do not bless idly.',
    options: [
      {
        id: 'carry', label: 'Carry the flame up the ridge',
        detail: 'Costs {hp} HP · your slowest creature gains +2 👟 Speed',
        cost: { hpPct: 0.08, hpFloor: 4 },
        // 🕯️ v7.39: no speed cap, so the keeper always has someone to bless.
        apply: (s) => {
          const slowest = [...s.roster].sort((a, b) => a.speed - b.speed)[0]
          if (slowest) raiseSpeed(slowest, 2)
        },
      },
      {
        id: 'refuse', label: 'Refuse gently',
        detail: 'He trades his old maps for water · +12 gold',
        apply: s => { s.gold += 12 },
      },
    ],
  },
  {
    id: 'grove', group: 'good', art: '/ui/events/grove.webp',
    name: 'The Molting Grove',
    body: 'Shed scales and feathers blanket the clearing, and the old growth hums with slow, patient life. There is room to rest — or to gather.',
    options: [
      {
        id: 'rest', label: 'Let one of yours rest here',
        detail: 'Clears ALL 🤕 injuries on a creature you pick',
        picker: 'creature',
        targetEligible: c => !!c.injured,
        requires: 'Needs an 🤕 injured creature',
        apply: (_s, [t]) => { if (t) { t.injuries = 0; t.injured = false } },
      },
      {
        id: 'harvest', label: 'Harvest the moltings',
        detail: '+1 🌿 Grass to two creatures you pick',
        picker: 'creature-pair',
        apply: (_s, targets) => { for (const t of targets) t.stats.grass += 1 },
      },
    ],
  },
  {
    id: 'rival', group: 'good', art: '/ui/events/rival.webp',
    name: 'The Rival Dispatcher',
    body: 'A rival crew lounges by the trail markers. Their captain wants a race — her champion against your pick, thirty gold on it — and grins at your roster.',
    options: [
      {
        id: 'race', label: 'Race them (pick your runner)',
        // v5.71: the 10-gold stake is now PRINTED — the wager grayed
        // silently for broke crews before (you cannot bet what you
        // cannot cover; the rival sizes up your purse, not your legs).
        detail: 'Stake 10 gold · 👟 Speed 3 wins +30, slower forfeits · runner gains +1 👟 Speed',
        eligible: s => s.gold >= 10,
        requires: 'Needs 10 gold to cover the wager',
        picker: 'creature',
        apply: (s, [t]) => {
          if (!t) return
          // 🏁 v7.39: judged on the Speed the roster shows (Swift Boots, Pacesetter, water) - not the bare stat.
          if (effectiveSpeed(t, s) >= RACE_WIN_SPEED) s.gold += 30
          else {
            s.goldSpent = (s.goldSpent ?? 0) + Math.min(s.gold, 10) // 🧘 v6.54: a lost wager is gold spent
            s.gold = Math.max(0, s.gold - 10)
          }
          raiseSpeed(t, 1)
        },
      },
      {
        id: 'decline', label: 'Decline the wager',
        detail: 'They jeer; nothing happens',
        apply: () => {},
      },
    ],
  },
  {
    id: 'storyteller', group: 'good', art: '/ui/events/storyteller.webp',
    name: 'The Storyteller’s Circle',
    body: 'Travelers trade tales around a fire that seems in no hurry to die. Your creatures’ ears are up. There is a seat open — and a hat going around.',
    options: [
      {
        id: 'boast', label: 'Boast of your victories',
        detail: 'One creature you pick gains +25 XP',
        picker: 'creature',
        apply: (s, [t]) => { if (t) grantXp(t, 25, s) },
      },
      {
        id: 'listen', label: 'Listen humbly all night',
        detail: 'Every creature gains +8 XP',
        apply: s => { for (const c of s.roster) grantXp(c, 8, s) },
      },
      {
        id: 'slip', label: 'Slip away early',
        detail: '+10 gold lifted from the hat',
        apply: s => { s.gold += 10 },
      },
    ],
  },
  // ── BAD: everything meaningful costs you ────────────────────────────
  {
    id: 'tithe', group: 'bad', art: '/ui/events/tithe.webp',
    name: 'The Tithe Collector',
    body: 'A robed figure blocks the pass, palm out and silent. There is nothing here to negotiate: the road runs one way, and that hand does not move.',
    options: [
      {
        id: 'pay', label: 'Pay the tithe',
        detail: 'Costs {gold} gold (25% of your purse)',
        cost: { goldPct: 0.25, goldFloor: 5 },
        apply: () => {},
      },
      {
        id: 'force', label: 'Force your way through',
        detail: 'Costs {hp} HP',
        cost: { hpPct: 0.12, hpFloor: 6 },
        apply: () => {},
      },
    ],
  },
  {
    id: 'grindstone', group: 'bad', art: '/ui/events/grindstone.webp',
    name: 'The Grindstone',
    body: 'An abandoned dwarven grindstone still spins true in the ruin, patient as the mountain. Edges for bulk — that was always the dwarves’ trade.',
    options: [
      {
        id: 'hone', label: 'Hone one creature’s claws',
        detail: '+1 ⚔️ Fight · −1 from its highest element',
        picker: 'creature',
        targetEligible: c => ELEMENTS.some(e => c.stats[e] > 0),
        requires: 'Needs a creature with an element to grind down',
        apply: (_s, [t]) => { if (!t) return; t.fight = (t.fight ?? 0) + 1; t.stats[highestElement(t)] -= 1 },
      },
      {
        id: 'sell', label: 'Sharpen your gear and sell it',
        detail: '+20 gold',
        apply: s => { s.gold += 20 },
      },
      { id: 'leave', label: 'Leave it spinning', detail: 'Nothing happens', apply: () => {} },
    ],
  },
  {
    id: 'idol', group: 'bad', art: '/ui/events/idol.webp',
    name: 'The Cracked Idol',
    body: 'A stone idol stands split down the middle, and both halves whisper — one of tribute, one of gold under its feet. Neither whisper sounds kind.',
    options: [
      {
        id: 'feed', label: 'Feed it gold',
        detail: 'Costs 10 gold · +2 🔥\u00a0💧\u00a0🌿\u00a0⚡\u00a0🪨 (you pick one) to one creature',
        cost: { gold: 10 },
        picker: 'creature-element',
        apply: (_s, [t], element) => { if (t && element) t.stats[element] += 2 },
      },
      {
        id: 'smash', label: 'Smash it for the offerings',
        detail: 'Costs {hp} HP · +15 gold from under its feet',
        cost: { hpPct: 0.08, hpFloor: 4 },
        injuryRider: 'your highest-level creature takes 1 🤕 injury',
        apply: s => {
          s.gold += 15
          const top = [...s.roster].sort((a, b) => b.level - a.level)[0]
          if (top) injureCreature(top, s)
        },
      },
      { id: 'back', label: 'Back away slowly', detail: 'Nothing happens', apply: () => {} },
    ],
  },
  {
    id: 'ferry', group: 'bad', art: '/ui/events/ferry.webp',
    name: 'The Night Ferry',
    body: 'A ferryman waits where the trail meets black water, pole already in hand. The crossing costs coin; the shore road costs blisters. Either way the night takes its fare.',
    options: [
      {
        // v5.71 (per Javi: "I would have rather paid the 15 gold"): the
        // old `eligible` gate hid this whenever nobody was hurt, forcing
        // the HP toll on a player who had coin in hand. Buying the
        // restful crossing IS the point; the healing is a bonus.
        id: 'board', label: 'Board the ferry',
        detail: 'Costs 15 gold · restful: every 🤕 injured creature heals 1 stack',
        cost: { gold: 15 },
        apply: s => {
          for (const c of s.roster) {
            const stacks = injuryStacks(c)
            if (stacks > 0) {
              c.injuries = stacks - 1
              c.injured = c.injuries > 0
            }
          }
        },
      },
      {
        id: 'shore', label: 'Walk the shore',
        detail: 'Costs {hp} HP',
        cost: { hpPct: 0.05, hpFloor: 2 },
        apply: () => {},
      },
    ],
  },
  {
    id: 'broker', group: 'bad', art: '/ui/events/broker.webp',
    name: 'The Soul Broker',
    body: 'A hooded spirit weighs your years on a balance of blue fire. Its prices are plain, its measures honest, its appetite endless.',
    options: [
      {
        id: 'trade', label: 'Trade a decade',
        detail: 'Costs {maxhp} MAX HP forever · +3 🔥\u00a0💧\u00a0🌿\u00a0⚡\u00a0🪨 (you pick one) to one creature',
        cost: { maxHpPct: 0.1, maxHpFloor: 5 },
        picker: 'creature-element',
        apply: (_s, [t], element) => { if (t && element) t.stats[element] += 3 },
      },
      {
        id: 'trade2', label: 'Trade two decades',
        detail: 'Costs {maxhp} MAX HP forever · one creature gains +1 ⚔️ Fight and +1 👟 Speed',
        cost: { maxHpPct: 0.15, maxHpFloor: 8 },
        picker: 'creature',
        apply: (_s, [t]) => {
          if (!t) return
          t.fight = (t.fight ?? 0) + 1
          raiseSpeed(t, 1)
        },
      },
      { id: 'refuse', label: 'Keep your years', detail: 'The scales tip empty; nothing happens', apply: () => {} },
    ],
  },
  {
    id: 'crown', group: 'bad', art: '/ui/events/crown.webp',
    name: 'The Hollow Crown',
    body: 'A dead king’s crown hums on a cairn of old stones. What it takes does not grow back; what it gives, it gives at once.',
    options: [
      {
        id: 'wear', label: 'Wear it',
        detail: 'Costs {maxhp} MAX HP forever · every creature gains +12 XP',
        cost: { maxHpPct: 0.08, maxHpFloor: 4 },
        apply: s => { for (const c of s.roster) grantXp(c, 12, s) },
      },
      {
        id: 'pry', label: 'Pry out the gems',
        detail: 'Costs {maxhp} MAX HP forever (it fights back) · +25 gold',
        cost: { maxHpPct: 0.04, maxHpFloor: 2 },
        apply: s => { s.gold += 25 },
      },
      { id: 'leave', label: 'Leave the dead their due', detail: 'Nothing happens', apply: () => {} },
    ],
  },
  {
    id: 'forge', group: 'bad', art: '/ui/events/forge.webp',
    name: 'The Ember Forge',
    body: 'A living forge crouches in the rock, coals breathing like a slow heart. It will temper one of yours — for fuel. It is not particular about what burns.',
    options: [
      {
        id: 'gold', label: 'Feed it your gear',
        detail: 'Costs 20 gold · one creature you pick gains +1 ⚔️ Fight',
        cost: { gold: 20 },
        picker: 'creature',
        apply: (_s, [t]) => { if (t) t.fight = (t.fight ?? 0) + 1 },
      },
      {
        id: 'vigor', label: 'Feed it your vigor',
        detail: 'Costs {hp} HP · one creature you pick gains +1 ⚔️ Fight',
        cost: { hpPct: 0.12, hpFloor: 6 },
        picker: 'creature',
        apply: (_s, [t]) => { if (t) t.fight = (t.fight ?? 0) + 1 },
      },
      { id: 'walk', label: 'Walk away', detail: 'The forge sighs; nothing happens', apply: () => {} },
    ],
  },
]

/** 🔣 v5.40 (per Javi): every choice wears a left-aligned icon, keyed
 * `${eventId}/${optionId}`. v5.72: Javi's THIRTY painted dioramas
 * landed — one per option, magenta-keyed to alpha. A path renders as
 * an <img>; a bare glyph would still render as text (the emoji era). */
export const OPTION_ICONS: Record<string, string> = {
  'bridge/climb': '/ui/eventicons/bridge-climb.webp',
  'bridge/walk': '/ui/eventicons/bridge-walk.webp',
  'beacon/carry': '/ui/eventicons/beacon-carry.webp',
  'beacon/refuse': '/ui/eventicons/beacon-refuse.webp',
  'grove/rest': '/ui/eventicons/grove-rest.webp',
  'grove/harvest': '/ui/eventicons/grove-harvest.webp',
  'rival/race': '/ui/eventicons/rival-race.webp',
  'rival/decline': '/ui/eventicons/rival-decline.webp',
  'storyteller/boast': '/ui/eventicons/storyteller-boast.webp',
  'storyteller/listen': '/ui/eventicons/storyteller-listen.webp',
  'storyteller/slip': '/ui/eventicons/storyteller-slip.webp',
  'tithe/pay': '/ui/eventicons/tithe-pay.webp',
  'tithe/force': '/ui/eventicons/tithe-force.webp',
  'grindstone/hone': '/ui/eventicons/grindstone-hone.webp',
  'grindstone/sell': '/ui/eventicons/grindstone-sell.webp',
  'grindstone/leave': '/ui/eventicons/grindstone-leave.webp',
  'idol/feed': '/ui/eventicons/idol-feed.webp',
  'idol/smash': '/ui/eventicons/idol-smash.webp',
  'idol/back': '/ui/eventicons/idol-back.webp',
  'ferry/board': '/ui/eventicons/ferry-board.webp',
  'ferry/shore': '/ui/eventicons/ferry-shore.webp',
  'broker/trade': '/ui/eventicons/broker-trade.webp',
  'broker/trade2': '/ui/eventicons/broker-trade2.webp',
  'broker/refuse': '/ui/eventicons/broker-refuse.webp',
  'crown/wear': '/ui/eventicons/crown-wear.webp',
  'crown/pry': '/ui/eventicons/crown-pry.webp',
  'crown/leave': '/ui/eventicons/crown-leave.webp',
  'forge/gold': '/ui/eventicons/forge-gold.webp',
  'forge/vigor': '/ui/eventicons/forge-vigor.webp',
  'forge/walk': '/ui/eventicons/forge-walk.webp',
}
export const eventById = (id: string | undefined | null): EventDef | null =>
  EVENTS.find(e => e.id === id) ?? null

/**
 * Draw an unseen event from the group's per-run bag; when the bag runs
 * dry the group reshuffles (same philosophy as the v5.17 decline bags).
 * The draw itself is random — outcomes never are.
 */
export function rollEvent(state: RunState, group: EventGroup): string {
  const pool = EVENTS.filter(e => e.group === group)
  const seen = new Set(state.seenEvents ?? [])
  let fresh = pool.filter(e => !seen.has(e.id))
  if (fresh.length === 0) {
    // reshuffle: forget THIS group's history only
    state.seenEvents = (state.seenEvents ?? []).filter(id => eventById(id)?.group !== group)
    fresh = pool
  }
  // v5.57: rand(), not Math.random — inside a daily's node stream the
  // draw is part of the skeleton (same mystery node, same event).
  const picked = fresh[Math.floor(rand() * fresh.length)]!
  state.seenEvents = [...(state.seenEvents ?? []), picked.id]
  return picked.id
}
